/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, useState, useRef, Fragment } from 'react';
import utils from 'utils';
import cmsUtils from '../../cmsUtils';
import env from '../../../../env';
import { useHistory } from "react-router-dom";
import { createPortal } from "react-dom";
import { AtdwSearchMain } from '../../../atdw/atdwSearchMain/atdwSearchMain';
import style from './atdwSearchOrMapStyle';

function Portal({ children }) {
    const [el] = useState(document.createElement('div'));
    useEffect(() => {
        let mount = document.getElementById("portal-root");
        if (!mount) {
            mount = document.createElement('div');
            mount.id = 'portal-root';
            document.body.appendChild(mount);
        }
        mount.appendChild(el);
        return () => mount.removeChild(el);
    }, [el]);
    return createPortal(children, el);
}

export function AtdwSearchOrMap(props) {
    const item = props.item;
    const cmsOption = props.cmsOption || {};
    const cssClass = utils.classNames('cms_item', 'atdw-search-or-map', item.cssClass || item.anchorName || '');
    
    // Get parameters from CMS
    const fromType = cmsUtils.payload(item, 'fromType') || 'area';
    const mapArea = cmsUtils.payload(item, 'mapArea') || 'all';
    
    // State management
    const [currentAtdwType, setCurrentAtdwType] = useState('ACCOMM');
    const [currentView, setCurrentView] = useState('map'); // 'search' or 'map'
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    

    
    // Map related states
    const [openPopup, setOpenPopup] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const mapItem = useRef();
    const history = useHistory();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownOpen && !event.target.closest('.dropdown')) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [dropdownOpen]);

    // Map functionality (similar to atdwMap.js)
    useEffect(() => {
        if (currentView !== 'map') return;

        console.log('Map useEffect triggered with:', { currentAtdwType, mapArea, fromType });

        const abortController = new AbortController();
        const signal = abortController.signal;
        let checkScript = true;
        let script = document.createElement('script');

        const fetchData = async () => {
            try {
                console.log('Fetching map data from:', env.apiBase + "/api/atdw/getmapproducts");
                console.log('Request body:', {
                    category: currentAtdwType,
                    mapArea: mapArea,
                    fromType: fromType
                });

                const res = await fetch(env.apiBase + "/api/atdw/getmapproducts", {
                    method: 'post',
                    signal: signal,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        category: currentAtdwType,
                        mapArea: mapArea,
                        fromType: fromType
                    })
                });

                console.log('API Response status:', res.status);

                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }

                const data = await res.json();
                console.log('Map data received:', data);

                if (window['google'] === undefined) {
                    checkScript = false;
                }
                else {
                    if (window['google'].maps === undefined) {
                        checkScript = true;
                    }
                }

                if (!checkScript) {
                    script.src = `https://maps.googleapis.com/maps/api/js?key=${env.googleMapKey}`;
                    script.async = true;
                    document.body.appendChild(script);
                    script.addEventListener('load', () => {
                        setupMap();
                    }, false);
                    setTimeout(() => {
                        setupMap();
                    }, 1000);
                }
                else {
                    setupMap();
                }

                function setupMap() {
                    console.log('Setting up map with items:', data?.results?.productResult?.products?.length || 0);
                    
                    if (!data?.results?.productResult?.products) {
                        console.error('No product data found in response');
                        return;
                    }

                    const items = data.results.productResult.products;
                    let center = { lat: -33.01612429303814, lng: 137.62082589888283 };
                    let zoom = 12;
                    let mapPath = null;

                    var map = new window.google.maps.Map(mapItem.current, {
                        center: center,
                        zoom: zoom,
                    });

                    if (mapPath) {
                        mapPath.setMap(map);
                    }

                    items.forEach(el => {
                        let geoCode = {
                            lat: Number(el.additionalData.googlemap.geocodeGdaLatitude),
                            lng: Number(el.additionalData.googlemap.geocodeGdaLongitude)
                        };
                        var marker = new window.google.maps.Marker({
                            position: { lat: geoCode.lat, lng: geoCode.lng },
                            map: map,
                            title: el.productName
                        });
                        var infowindow = new window.google.maps.InfoWindow({
                            content: '<div class="info__window">' +
                                '<div class="info__title">' + el.productName + '</div>' +
                                '<div class="info__town">' + el.town + '</div>' +
                                '<div class="info__town">' + el.address + ' ' + el.town + '</div>' +
                                '<p class="info__desc">' + el.shortDesc + '</p>' +
                                '</div>'
                        });

                        var isMobile = false;
                        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                            isMobile = true;
                        }

                        marker.addListener('mouseover', function () {
                            if (!isMobile) {
                                infowindow.open(map, marker);
                            }
                        });
                        marker.addListener('mouseout', function () {
                            if (!isMobile) {
                                infowindow.close();
                            }
                        });
                        marker.addListener('click', function () {
                            if (isMobile) {
                                setPopupData({
                                    productName: el.productName,
                                    address: el.address,
                                    town: el.town,
                                    desc: el.shortDesc,
                                    slug: el.slug
                                });
                                setOpenPopup(true);
                            }
                            else {
                                history.push('/atdwproduct/' + el.slug);
                            }
                        });
                    });
                }
            } catch (error) {
                console.error('Error fetching map data:', error);
                console.error('Error details:', {
                    message: error.message,
                    stack: error.stack
                });
            }
        }

        fetchData();
        return function cleanup() {
            abortController.abort();
            if (!checkScript) {
                document.body.removeChild(script);
            }
        }
    }, [currentAtdwType, currentView]);

    // Filter options
    const filterOptions = [
        { value: 'ACCOMM', label: 'ACCOMMODATION' },
        { value: 'ATTRACTION', label: 'ATTRACTIONS' },
        { value: 'RESTAURANT', label: 'EAT & DRINK' },
        { value: 'EVENT', label: 'EVENTS' },
        { value: 'HIRE', label: 'HIRE' },
        { value: 'INFO', label: 'INFO' },
        { value: 'TOUR', label: 'TOURS' },
        { value: 'GENSERVICE', label: 'GENERAL' }
        // { value: 'CUTTLEFEST', label: 'CUTTLEFEST' }
    ];

    const currentFilter = filterOptions.find(option => option.value === currentAtdwType);

    // Handle view switching with animation
    const handleViewChange = (newView) => {
        if (newView === currentView) return;
        
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentView(newView);
            setTimeout(() => {
                setIsTransitioning(false);
            }, 50);
        }, 150);
    };

    const TypeFilter = () => (
        <div css={style.filters} className="atdw__searchormap__filter">
            <div className="dropdown">
                <button 
                    css={style.dropdownButton}
                    className="btn dropdown-toggle" 
                    type="button" 
                    id="atdwSearchOrMapDropdown" 
                    data-bs-toggle="dropdown" 
                    aria-expanded={dropdownOpen}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    style={{backgroundColor: 'transparent', border: 'none'}}
                >
                    {currentFilter ? currentFilter.label : 'Select Filter'}
                </button>
                <ul 
                    className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`} 
                    aria-labelledby="atdwSearchOrMapDropdown"
                >
                    {filterOptions.map((option) => (
                        <li key={option.value}>
                            <a 
                                className={`dropdown-item ${currentAtdwType === option.value ? 'active' : ''}`}
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setCurrentAtdwType(option.value);
                                    setDropdownOpen(false);
                                }}
                            >
                                {option.label}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );

    const ViewToggle = () => (
        <div css={style.viewToggle} className="atdw__searchormap__toggle">
            <button 
                className={`btn ${currentView === 'search' ? 'btn-primary' : 'btn-outline-primary'}`}
                // classNames="btn btn-primary"
                onClick={() => handleViewChange('search')}
                style={{ marginRight: '0.5rem' }}
                disabled={isTransitioning}
            >
                <img 
                    src={utils.site.resourcePath('/assets/icons/icon-grid.png')} 
                    alt="Search"
                />
                Grid
            </button>
            <button 
                className={`btn ${currentView === 'map' ? 'btn-primary' : 'btn-outline-primary'}`}
                // classNames="btn btn-primary"
                onClick={() => handleViewChange('map')} 
                disabled={isTransitioning}
            >
                <img 
                    src={utils.site.resourcePath('/assets/icons/icon-australia.png')} 
                    alt="Map"
                />
                Map
            </button>
        </div>
    );

    return (
        <Fragment>
            <Portal>
                {
                    openPopup &&
                    <Fragment>
                        <div css={style.popupBackground}></div>
                        <div css={style.container}>
                            <div css={style.popupTitle}> {popupData.productName} </div>
                            <div css={style.popupTown}> {popupData.town} </div>
                            <div css={style.popupTown}> {popupData.address + ' ' + popupData.town} </div>
                            <p css={style.popupDesc}> {popupData.desc} </p>
                            <div style={{ textAlign: 'center' }}>
                                <button className="btn btn-primary" onClick={() => {
                                    history.push('/atdwproduct/' + popupData.slug);
                                }}>Go</button>
                                <button className="btn btn-primary" style={{ marginLeft: '1rem' }} onClick={() => {
                                    setOpenPopup(false);
                                    setPopupData(null);
                                }} >Close</button>
                            </div>
                        </div>
                    </Fragment>
                }
            </Portal>
            
            <section 
                style={{ 
                    padding: '1rem 0px', 
                    maxWidth: "1200px", 
                    margin: "auto" 
                }} 
                className={cssClass} 
                data-cms-item-id={cmsOption.isCmsEdit && item.itemId} 
                id={item.anchorName}
            >
                <div className="container">
                    {/* Controls */}
                    <div css={style.controls} className="d-flex justify-content-between align-items-center mb-3">
                        <TypeFilter />
                        <ViewToggle />
                    </div>
                    
                    {/* Content */}
                    <div css={style.contentContainer}>
                        {currentView === 'search' ? (
                            <div 
                                css={style.contentView} 
                                className={`${!isTransitioning ? 'active' : ''}`}
                            >
                                <div css={style.searchView}>
                                    <AtdwSearchMain 
                                        atdwType={currentAtdwType} 
                                        subTypes="" 
                                        autoSearch="on" 
                                        hideSearch="on" 
                                        numOfTileLines="12" 
                                        town="ALL" 
                                        hideCount="on"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div 
                                css={style.contentView} 
                                className={`${!isTransitioning ? 'active' : ''}`}
                            >
                                <div css={style.atdwMap} className="atdw-map-container">
                                    <div ref={mapItem} style={{ height: "100%" }}></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </Fragment>
    )
} 